/**
 * ThemeSwitcher Component
 *
 * UI for selecting and switching between available themes.
 * Displays:
 * - Theme name and description
 * - Color preview swatches (7 colors)
 * - Radio button selection
 *
 * Integrates with ThemeService for theme loading and persistence.
 */

import { useState, useEffect } from 'react';
import { ThemeService, type ThemeMetadata } from '../services/themeService';
import ContextHelp from '../easymap/ContextHelp/ContextHelp';
import './ThemeSwitcher.scss';

export function ThemeSwitcher() {
  const [currentTheme, setCurrentTheme] = useState<string>('');
  const [availableThemes, setAvailableThemes] = useState<ThemeMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load current theme and available themes
    const current = ThemeService.getCurrentTheme();
    const themes = ThemeService.getAvailableThemes();

    setCurrentTheme(current);
    setAvailableThemes(themes);
  }, []);

  const handleThemeChange = async (themeId: string) => {
    if (themeId === currentTheme) {
      return; // Already selected
    }

    setIsLoading(true);
    setError(null);

    try {
      await ThemeService.loadTheme(themeId);
      setCurrentTheme(themeId);
    } catch (err) {
      console.error('Failed to load theme:', err);
      setError('Failed to load theme. Please try again.');
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  // Group themes by category
  const themesByCategory = availableThemes.reduce(
    (acc, theme) => {
      if (!acc[theme.category]) {
        acc[theme.category] = [];
      }
      acc[theme.category].push(theme);
      return acc;
    },
    {} as Record<string, ThemeMetadata[]>
  );

  const categoryLabels: Record<string, string> = {
    base: 'Base Themes',
    seasonal: 'Seasonal Themes',
    accessibility: 'Accessibility Themes',
    variant: 'Variant Themes',
    custom: 'Custom Themes',
  };

  return (
    <div className="theme-switcher">
      <div className="theme-switcher-header">
        <h3><ContextHelp title='Help with Themes' context='theme-help' /> Select Theme</h3>
        {isLoading && <span className="theme-switcher-loading">Switching...</span>}
      </div>

      {error && <div className="theme-switcher-error">{error}</div>}

      <div className="theme-switcher-content">
        {Object.entries(themesByCategory).map(([category, themes]) => (
          <div key={category} className="theme-category">
            <h4 className="theme-category-label">{categoryLabels[category] || category}</h4>

            {themes.map(theme => (
              <div
                key={theme.id}
                className={`theme-option ${currentTheme === theme.id ? 'theme-option-active' : ''}`}
              >
                <label className="theme-option-label">
                  <input
                    type="radio"
                    name="theme"
                    value={theme.id}
                    checked={currentTheme === theme.id}
                    onChange={() => handleThemeChange(theme.id)}
                    disabled={isLoading}
                    className="theme-option-radio"
                  />

                  <div className="theme-option-content">
                    <div className="theme-option-info">
                      <strong className="theme-option-name">{theme.name}</strong>
                      {theme.description && (
                        <p className="theme-option-description">{theme.description}</p>
                      )}
                    </div>

                    <div className="theme-option-preview">
                      {theme.colorPreview.map(color => (
                        <span
                          key={color.name}
                          className="color-swatch"
                          style={{ backgroundColor: color.value }}
                          title={`${color.name}: ${color.value}`}
                          aria-label={`${color.name} color: ${color.value}`}
                        />
                      ))}
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>

      {availableThemes.length === 0 && (
        <div className="theme-switcher-empty">No themes available.</div>
      )}
    </div>
  );
}
